import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { Configuration } from '@shared/models/configuration';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { getAccessToken } from '@shared/store/selectors/security.selector';
import { AuthApiService } from '@core/services/auth-api.service';
import { ItpDesktopService } from '@core/services/itpdesktopservices/itp-desktop.service';
import {
  accessTokenCookieName, COOKIE_EXPIRY_COOKIE, forgetSessionCookie, isSessionCookieLost, lostSessionLogoutUrl,
  markSessionCookie, readSessionCookieMark, sessionCookieExpiresAt
} from './session-cookie';
import { isSqaRunMode } from './sqa-run-mode';

/**
 * ITP2-14341 (Plan ITP-P-65). Once the app is signed in, its session cookie is the session: when the cookie this tab
 * saw disappears before its own expiry, the app runs the normal full logout (AuthApiService.logoutUser — the server
 * revokes the token, every tab is told, and the IdP session ends, so a reload does not sign back in) instead of
 * carrying on. Checked on every HTTP request (SessionCookieInterceptor), and when the tab is focused or shown.
 * The boot half is endLostSessionAtBoot in session-cookie.ts.
 *
 * Never logs out:
 * - a tab that has not seen the cookie (no mark): a cookie domain the browser refused, a dev dummy token
 *   (Configuration.UsingDummyToken — that path writes no cookie), ITP Desktop (its own host keeps the token;
 *   `isDesktop` is latched, since deleting cookies there would also delete the `isDesktop` cookie itself);
 * - an SQA run (isSqaRunMode): the harness drives its own cookies and sign-in;
 * - a session that is not signed in, or one already logging out (AuthApiService.isLogoutUserStarting — also set by
 *   switchCompany), or whose cookies were removed on purpose (deleteCookieInfo forgets the mark);
 * - a cookie that reached its own expiry: the next request refreshes the token and the refresh rewrites the cookie.
 * Demo and other temp access tokens (TempAccessTokenService) ride inside the user's own session and leave the user's
 * cookie in place; magic-link ShareId/Context sessions sign in through the same exchange. Both follow the rule.
 */
@Injectable({ providedIn: 'root' })
export class SessionCookieGuardService implements OnDestroy {
  private authenticated = false;
  private desktop: boolean;
  private readonly subscription: Subscription;
  private readonly onFocus = () => this.endSessionIfCookieLost();
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.endSessionIfCookieLost();
    }
  };

  constructor(store: Store<SecurityState>,
              private cookieService: CookieService,
              private authService: AuthApiService,
              private itpDesktopService: ItpDesktopService) {
    this.desktop = this.itpDesktopService.isDesktop();
    // `undefined` is the gap while the interceptor refreshes the token (handle401Error): still signed in.
    this.subscription = store.select(getAccessToken).subscribe(accessToken => {
      if (accessToken !== undefined) {
        this.authenticated = !!accessToken?.access_token;
      }
    });
    window.addEventListener('focus', this.onFocus);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /**
   * True when this tab's session cookie is gone before its expiry and the full logout has been started; the caller
   * (a request) is then dropped. Otherwise false, and — when the cookie is there — the tab's mark is brought up to
   * date (a sign-in or a refresh in any tab writes a new expiry).
   */
  endSessionIfCookieLost(): boolean {
    if (!this.authenticated || this.authService.isLogoutUserStarting || this.isExempt()) {
      return false;
    }
    const mark = readSessionCookieMark();
    const prefix = mark?.prefix ?? Configuration.CookiePrefix;
    if (this.cookieService.get(accessTokenCookieName(prefix))) {
      this.remember(prefix, mark);
      return false;
    }
    if (!isSessionCookieLost(mark, name => this.cookieService.get(name), Date.now())) {
      return false;
    }
    console.log('[SessionCookieGuard] the session cookie was removed - logging out');
    forgetSessionCookie();
    this.authService.logoutUser();
    return true;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    window.removeEventListener('focus', this.onFocus);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private isExempt(): boolean {
    return this.desktop || Configuration.UsingDummyToken || isSqaRunMode();
  }

  private remember(prefix: string, mark: ReturnType<typeof readSessionCookieMark>): void {
    const expiresAt = sessionCookieExpiresAt(this.cookieService.get(prefix + COOKIE_EXPIRY_COOKIE));
    const logoutUrl = lostSessionLogoutUrl(mark);
    if (mark && mark.expiresAt === expiresAt && mark.logoutUrl === logoutUrl) {
      return;
    }
    // A desktop host may set its cookie after this service was made: look once more before the first mark.
    if (!mark && this.itpDesktopService.isDesktop()) {
      this.desktop = true;
      return;
    }
    markSessionCookie({ prefix, expiresAt, logoutUrl });
  }
}
